// components/projects/FilePreview.js - OPRAVENÉ s PDF podporou
import React, { useState, useEffect } from 'react';
import { X, Download, Calendar, User, File, Loader } from 'lucide-react';
import { getFileTypeInfo } from '../../utils/fileUtils';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../services/api.js';
import { downloadFile } from '../../utils/downloadUtils';

const FilePreview = ({ isOpen, onClose, file, projectId, signature = null }) => {
    const { t } = useTranslation();
    const [fileUrl, setFileUrl] = useState(null);  // ✅ Přejmenováno z imageUrl na fileUrl
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    
    // ✅ Determine if we're showing a signature
    const isSignature = !file && signature;
    const displayItem = signature || file;
    
    // ✅ R2 File Loading Effect (podporuje image i PDF)
    useEffect(() => {
        if (!isOpen || (!file && !signature)) {
            // Cleanup when modal closes
            if (fileUrl && fileUrl.startsWith('blob:')) {
                URL.revokeObjectURL(fileUrl);
            }
            setFileUrl(null);
            setIsLoading(false);
            setError(false);
            return;
        }

        const loadContent = async () => {
            // ✅ OPTIMALIZACE: Use preloaded blob URL if available (prevents re-download)
            const preloadedUrl = signature?.preloadedBlobUrl || file?.preloadedBlobUrl;
            if (preloadedUrl) {
                console.log('🚀 FilePreview: Using preloaded blob URL (no download needed)');
                setFileUrl(preloadedUrl);
                return;
            }

            // ✅ Handle signature loading
            if (isSignature) {
                const sig = signature;

                // If signature has base64, use it (backward compatibility)
                if (sig.base64) {
                    console.log('📷 FilePreview: Using base64 signature data');
                    setFileUrl(sig.base64);
                    return;
                }

                // ⚠️ Signature MUST be fresh (no cache) to show updates
                console.log('📷 FilePreview: Loading fresh signature from R2 (cache-busting)');
                setIsLoading(true);
                setError(false);

                try {
                    const token = localStorage.getItem('token');
                    const apiBase = API_BASE_URL;

                    const cacheBuster = `?t=${Date.now()}`;
                    const response = await fetch(`${apiBase}/api/projects/${projectId}/signature/download${cacheBuster}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        cache: 'no-store'
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    setFileUrl(blobUrl);
                    setIsLoading(false);

                    console.log('✅ FilePreview: Fresh signature loaded from R2');
                } catch (error) {
                    console.error('❌ FilePreview: Failed to load signature from R2:', error);
                    setError(true);
                    setIsLoading(false);
                }
            } else if (file) {
                // ✅ Handle file loading (images and PDFs)
                const fileType = file.fileType || file.file_type || '';
                const isImageFile = fileType.startsWith('image/') || !fileType;
                const isPdfFile = fileType === 'application/pdf';

                // ✅ UPDATED: Load both images AND PDFs from R2 with cache support
                if (isImageFile || isPdfFile) {
                    console.log(`📷 FilePreview: Loading ${isPdfFile ? 'PDF' : 'image'} file from R2 (cacheable)`);
                    setIsLoading(true);
                    setError(false);

                    try {
                        const token = localStorage.getItem('token');
                        const apiBase = API_BASE_URL;

                        const fileId = file.id || file._id;
                        const response = await fetch(`${apiBase}/api/projects/${projectId}/file/${fileId}/download`, {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                            // ✅ Allow browser & CDN caching for files (they don't change)
                        });

                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}`);
                        }

                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        setFileUrl(blobUrl);
                        setIsLoading(false);

                        console.log(`✅ FilePreview: ${isPdfFile ? 'PDF' : 'Image'} file loaded from R2`);
                    } catch (error) {
                        console.error(`❌ FilePreview: Failed to load ${isPdfFile ? 'PDF' : 'image'} file from R2:`, error);
                        setError(true);
                        setIsLoading(false);
                    }
                } else {
                    console.log('📄 FilePreview: Non-previewable file type, showing metadata only');
                }
            }
        };

        loadContent();
    }, [isOpen, file, signature, projectId, isSignature]);

    // Don't render if not open
    if (!isOpen) return null;

    // Get file info for non-signature files
    const fileInfo = !isSignature && file ? getFileTypeInfo(file.fileType || file.file_type || '') : null;
    const fileType = !isSignature && file ? (file.fileType || file.file_type || '') : '';
    const isImageFile = fileType.startsWith('image/') || (!fileType && !isSignature);
    const isPdfFile = fileType === 'application/pdf';
    const isPreviewableFile = isImageFile || isPdfFile;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="relative max-w-6xl w-full h-full flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center text-white mb-4 bg-black bg-opacity-50 p-4 rounded-t-lg">
                    <div className="flex items-center space-x-4">
                        <div>
                            <h3 className="text-lg font-medium">
                                {isSignature 
                                    ? t('projects.signature.title', 'Customer Signature')
                                    : (file?.originalName || file?.fileName || file?.file_name || 'File Preview')
                                }
                            </h3>
                            {!isSignature && file && (
                                <p className="text-sm text-gray-300">
                                    {fileInfo?.displayType || 'File'} • {fileType}
                                </p>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        {/* Download button */}
                        {!isSignature && file && (
                            <button
                                onClick={() => downloadFile(file, projectId)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded flex items-center space-x-2"
                            >
                                <Download className="h-4 w-4" />
                                <span>{t('files.download', 'Download')}</span>
                            </button>
                        )}
                        
                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-300 p-2"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                {/* Content area */}
                <div className="flex-1 flex items-center justify-center overflow-hidden">
                    <div className="max-w-full max-h-full flex items-center justify-center">
                        {/* Loading state */}
                        {isLoading && (
                            <div className="flex flex-col items-center space-y-4 text-white">
                                <Loader className="h-8 w-8 animate-spin" />
                                <p>
                                    {isSignature 
                                        ? t('projects.signature.loading', 'Loading signature...')
                                        : t('files.loading', 'Loading file...')
                                    }
                                </p>
                            </div>
                        )}

                        {/* Error state */}
                        {error && !isLoading && (
                            <div className="text-center text-white">
                                <File className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <p className="text-xl mb-2">
                                    {t('files.errors.loadFailed', 'Failed to load file')}
                                </p>
                                <p className="text-gray-400">
                                    {t('files.errors.tryAgain', 'Please try again or download the file directly.')}
                                </p>
                                {!isSignature && file && (
                                    <button
                                        onClick={() => downloadFile(file, projectId)}
                                        className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center space-x-2 mx-auto"
                                    >
                                        <Download className="h-4 w-4" />
                                        <span>{t('files.download', 'Download')}</span>
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Content display */}
                        {!isLoading && !error && fileUrl && (isImageFile || isSignature) && (
                            <img
                                src={fileUrl}
                                alt={isSignature ? t('projects.signature.title', 'Customer Signature') : (file?.originalName || file?.fileName || 'File')}
                                className="max-w-full max-h-full object-contain"
                                style={{ maxHeight: 'calc(100vh - 200px)' }}
                            />
                        )}

                        {/* PDF display */}
                        {!isLoading && !error && fileUrl && isPdfFile && (
                            <iframe
                                src={fileUrl}
                                title={file?.originalName || file?.fileName || 'PDF Document'}
                                className="w-full h-full border-0"
                                style={{ minHeight: 'calc(100vh - 200px)' }}
                            />
                        )}

                        {/* Non-previewable files */}
                        {!isLoading && !error && file && !isPreviewableFile && !isSignature && (
                            <div className="text-center text-white max-w-md">
                                <File className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <h3 className="text-xl mb-2">
                                    {file.originalName || file.fileName || file.file_name || 'File'}
                                </h3>
                                <p className="text-gray-300 mb-4">
                                    {fileInfo?.displayType || 'File'} • {file.fileType || file.file_type || 'Unknown type'}
                                </p>
                                
                                {/* File metadata */}
                                <div className="space-y-2 text-sm text-gray-400 mb-6">
                                    {file.uploadDate && (
                                        <div className="flex items-center justify-center space-x-2">
                                            <Calendar className="h-4 w-4" />
                                            <span>{new Date(file.uploadDate).toLocaleDateString()}</span>
                                        </div>
                                    )}
                                    {file.uploadedBy && (
                                        <div className="flex items-center justify-center space-x-2">
                                            <User className="h-4 w-4" />
                                            <span>{file.uploadedBy.name || file.uploadedBy}</span>
                                        </div>
                                    )}
                                    {(file.size || file.file_size) && (
                                        <div className="text-center">
                                            <span>{((file.size || file.file_size) / 1024).toFixed(1)} KB</span>
                                        </div>
                                    )}
                                </div>
                                
                                <p className="text-blue-400 mb-4">
                                    {t('files.cannotPreview', 'This file type cannot be previewed directly. Please download the file to view it.')}
                                </p>
                                
                                {/* ✅ KLÍČOVÁ OPRAVA: Download button používá downloadFile funkci */}
                                <button
                                    onClick={() => downloadFile(file, projectId)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded flex items-center space-x-2 mx-auto"
                                >
                                    <Download className="h-5 w-5" />
                                    <span>{t('files.download', 'Download File')}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer with file/signature details */}
                {displayItem && !isLoading && !error && (isImageFile || isPdfFile || isSignature) && (
                    <div className="text-white p-4 bg-black bg-opacity-50 rounded-b-lg">
                        <div className="flex justify-between items-center text-sm">
                            <div className="flex items-center space-x-4">
                                {!isSignature && file && (
                                    <>
                                        {file.uploadDate && (
                                            <div className="flex items-center space-x-1">
                                                <Calendar className="h-4 w-4" />
                                                <span>{new Date(file.uploadDate).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                        {file.uploadedBy && (
                                            <div className="flex items-center space-x-1">
                                                <User className="h-4 w-4" />
                                                <span>{file.uploadedBy.name || file.uploadedBy}</span>
                                            </div>
                                        )}
                                        {(file.size || file.file_size) && (
                                            <div>
                                                <span>{((file.size || file.file_size) / 1024).toFixed(1)} KB</span>
                                            </div>
                                        )}
                                    </>
                                )}
                                {isSignature && signature && (
                                    <div className="flex items-center space-x-1">
                                        <Calendar className="h-4 w-4" />
                                        <span>
                                            {signature.date 
                                                ? new Date(signature.date).toLocaleDateString()
                                                : t('projects.signature.noDate', 'No date available')
                                            }
                                        </span>
                                    </div>
                                )}
                            </div>
                            
                            {!isSignature && file && (
                                <div className="flex items-center space-x-2">
                                    <button
                                        onClick={() => downloadFile(file, projectId)}
                                        className="text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                                    >
                                        <Download className="h-4 w-4" />
                                        <span>{t('files.download', 'Download')}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilePreview;